import styled from '@emotion/styled'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as SaveIcon } from '../../../../assets/icons/save-icon-white-16.icon.svg'
import { ReactComponent as ShareIcon } from '../../../../assets/icons/share-icon-white-16.icon.svg'
import { ReactComponent as ArrowTopIcon } from '../../../../assets/icons/app/arrow-top.icon.svg'
import { BookInterface } from '../../../../core/interface/book.interface'
import { WorkMirrorBookInterface } from '../../../../core/interface/report/work-mirror-book.interface'
import { AnalyticsPermissionEnum } from '../../../../core/enums/analytics-permission.enum'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { analyticsDetailV2StateSetBooksInputsMenuOpen } from '../../../../store/reducers/analytics-details/analytic-details-v2.reducer'
import {
  selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  selectAnalyticDetailsStateBooksInputsMenuOpen,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import SearchTypeToggle from '../../search-bar-components/SearchTypeToggle'
import BooksInput from '../../search-bar-components/book/BooksInput'
import { AnalyticsTypeEnum } from '../../../../core/enums/analytics-type.enum'
import ClilFilterAction from '../../search-bar-components/book/ClilFilterAction'
import { selectWebAppStateMySelfHasPrivateReportsAccess } from '../../../../store/getters/web-app.getters'
import {
  buildRelativePath,
  PathConfig,
} from '../../../../core/config/PathConfig'

const CardStyle = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 56px;
  width: 360px;
  background-color: black;
  border-radius: 8px;
  padding: 8px;
  z-index: 10;
`

const HeaderStyle = styled.div``

const ButtonOpenMenuStyle = styled.button`
  transform: ${({ open }: { open: boolean }) =>
    open ? 'rotate(0deg)' : 'rotate(180deg)'};
`

const WMStyle = styled.span`
  color: #8dda86;
  text-align: center;
  /* Caption/C1 - Medium - Uppercase */
  font-family: Fira Sans, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.24px;
  text-transform: uppercase;
`

const AnalyticDetailsBooksInput = ({
  isLoadingNewConstellationGetBookReco,
  isLoadingAdd,
  isLoadingRemove,
  needSave,
  canShare,
  booksInput,
  handleSaveAnalytics,
  handleShareAnalytics,
  handleAddBookToAnalytic,
  handleRemoveBookInput,
  analyticsType,
  bookDetailsSelected,
  handleBookSelected,
  permission,
}: {
  isLoadingNewConstellationGetBookReco: boolean
  isLoadingAdd: boolean
  isLoadingRemove: boolean
  needSave: boolean
  canShare: boolean
  booksInput: BookInterface[]
  handleSaveAnalytics: () => void
  handleShareAnalytics: () => void
  handleAddBookToAnalytic: (eanId: number) => void
  handleRemoveBookInput: (eanId: number) => void
  analyticsType: AnalyticsTypeEnum
  bookDetailsSelected: WorkMirrorBookInterface | null
  handleBookSelected: (book: WorkMirrorBookInterface | null) => void
  permission?: AnalyticsPermissionEnum
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const booksInputsMenuOpen = useAppSelector(
    selectAnalyticDetailsStateBooksInputsMenuOpen,
  )

  const authorsInput = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  )

  const hasPrivateReportsAccess = useAppSelector(
    selectWebAppStateMySelfHasPrivateReportsAccess,
  )

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    dispatch(analyticsDetailV2StateSetBooksInputsMenuOpen(!booksInputsMenuOpen))
  }

  const handleOnClickSaveAnalytics = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    handleSaveAnalytics()
  }

  const handleOnClickShareAnalytics = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    handleShareAnalytics()
  }

  return (
    <CardStyle className="flex flex-col gap-[8px]">
      {hasPrivateReportsAccess &&
        analyticsType !== AnalyticsTypeEnum.WORK_MIRROR &&
        booksInput.length <= 0 &&
        authorsInput.length <= 0 && (
          <SearchTypeToggle
            analyticsType={analyticsType}
            onToggleButtonClick={() => {
              const path = buildRelativePath([
                PathConfig.BOOKMETRIE,
                PathConfig.PRIVATE_REPORTS,
                PathConfig.ANALYTICS,
                `new?type=${AnalyticsTypeEnum.PRIVATE_AUTHOR_MIRROR}`,
              ])

              navigate(path)
            }}
          />
        )}
      {analyticsType === AnalyticsTypeEnum.WORK_MIRROR &&
        booksInput.length <= 0 &&
        authorsInput.length <= 0 && (
          <SearchTypeToggle
            analyticsType={analyticsType}
            onToggleButtonClick={() => {
              const path = buildRelativePath([
                PathConfig.BOOKMETRIE,
                PathConfig.ANALYTICS,
                `new?type=${AnalyticsTypeEnum.AUTHOR_MIRROR}`,
              ])

              navigate(path)
            }}
          />
        )}
      <HeaderStyle className="flex flex-row justify-between items-center h-full">
        <div className="flex flex-row justify-start items-center h-[40px] gap-[4px]">
          {(permission === AnalyticsPermissionEnum.EDIT && (
            <>
              <button
                type="button"
                className="rounded p-[4px] text-white disabled:text-[#e3e3e366] disabled:bg-black hover:bg-[#252525] active:bg-[#e3e3e333]"
                disabled={!booksInput.length}
                onClick={handleOnClickSaveAnalytics}
              >
                <div className="relative">
                  <SaveIcon className="w-4 h-4" />
                  {(needSave && booksInput.length && (
                    <div className="absolute bg-[#F97E60] w-[4px] h-[4px] top-[-2px] right-[-2px] rounded-2xl" />
                  )) ||
                    null}
                </div>
              </button>
              <button
                type="button"
                className="rounded p-[4px] text-white disabled:text-[#e3e3e366] disabled:bg-black hover:bg-[#252525] active:bg-[#e3e3e333]"
                disabled={!canShare || !booksInput.length}
                onClick={handleOnClickShareAnalytics}
              >
                <ShareIcon className="w-4 h-4" />
              </button>
            </>
          )) ||
            null}
        </div>

        <WMStyle>{t('worksMirrors')}</WMStyle>
        <div
          className="flex flex-col items-center justify-around"
          style={{
            minHeight: 40,
            maxHeight: 40,
          }}
        >
          <ButtonOpenMenuStyle
            type="button"
            open={booksInputsMenuOpen}
            onClick={handleOpenMenu}
          >
            <ArrowTopIcon className="text-[#f4f4f4] w-5 h-5" />
          </ButtonOpenMenuStyle>
        </div>
      </HeaderStyle>
      <BooksInput
        permission={permission}
        booksInput={booksInput}
        isLoadingAdd={isLoadingAdd}
        isLoadingRemove={isLoadingRemove}
        handleBookSelected={handleBookSelected}
        bookDetailsSelected={bookDetailsSelected}
        booksInputsMenuOpen={booksInputsMenuOpen}
        handleRemoveBookInput={handleRemoveBookInput}
        handleAddBookToAnalytic={handleAddBookToAnalytic}
        isLoadingNewConstellationGetBookReco={
          isLoadingNewConstellationGetBookReco
        }
      />
      <ClilFilterAction />
    </CardStyle>
  )
}

export default AnalyticDetailsBooksInput
